import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";
import { Button } from "reactstrap";

import useQueriedRecords from "../../../../api/api-hooks/records/useQueriedRecords";
import useRecordMetadata from "../../../../hooks/useRecordMetadata";
import { BrieGPTTabs } from "../BrieGPTSidebarElements/Types";
import useRecordImage from "../../../../hooks/useRecordImage";
import { tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import RecordNameCell from "../../../Records/RecordNameCell";
import { Person } from "../../../../types/GlobalInterfaces";
import useUpdateProject from "../useUpdateGPTProject";
import { BrieGPTProject } from "../useBrieGPTItems";
import Member from "./Member";

interface ProjectMembersModalProps {
  open: boolean;
  onClose: () => void;
  project: BrieGPTProject;
  isOwner: boolean;
  user: Person;
  tab: BrieGPTTabs;
  setTab: Dispatch<SetStateAction<BrieGPTTabs>>;
  /** The View or Record ID which the Project is associated with. */
  itemID: string;
}

const query = {
  Fields: ["id", "_RecordName", "Email", "_TenantUser"],
  Filters: [
    {
      Group: true,
      Filters: [
        {
          Field: "_TenantUser",
          Method: "equals",
          Value: true,
        },
      ],
    },
  ],
};

function ProjectMembersModal({
  open,
  onClose,
  project,
  isOwner,
  user,
  tab,
  setTab,
  itemID,
}: ProjectMembersModalProps) {
  const client = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom) || "";
  const [item, setItem] = useState<string>("");
  const [newMembers, setNewMembers] = useState<any[]>([]);
  const [deletedMembers, setDeletedMembers] = useState<string[]>([]);
  const { updateProject, updating } = useUpdateProject(tab);
  const { metadataField, metadataFieldType } = useRecordMetadata({
    moduleAPIName: "Contacts",
  });
  const { imageField, imageFieldPresentation } = useRecordImage({
    moduleAPIName: "Contacts",
  });

  const projectMembers = useMemo(() => {
    const members = project?.Members?.map((m) => m.Member) || [
      {
        id: project?.CreatedBy,
        _RecordName: user?._RecordName,
        _ModuleAPIName: "Contacts",
      },
    ];

    return members;
  }, [project?.Members]);

  const finalMembers = useMemo(() => {
    return projectMembers
      .filter((m) => !deletedMembers.includes(m.id))
      .concat(newMembers);
  }, [deletedMembers, newMembers, projectMembers]);

  const { records, recordsLoading } = useQueriedRecords({
    moduleAPIName: "Contacts",
    query,
    disabled: !open,
  });

  const availableUsers = useMemo(() => {
    return records
      ?.filter((r: any) => !finalMembers.find((pm) => pm.id === r.id))
      ?.map((r: any) => ({
        label: [r?._RecordName || "(No Name)", r?.Email?.[0]?.Email || ""],
        value: r?.id,
        record: r,
      }));
  }, [records, finalMembers]);

  const members = useMemo(() => {
    return records?.filter((r: any) =>
      finalMembers.find((pm) => pm.id === r.id)
    );
  }, [records, finalMembers]);

  if (!open) return null;

  return (
    <Modal size={"sm"} open={open} onClose={handleClose} backdrop={"static"}>
      <div className="tw:px-6">
        <Modal.Header>
          <Modal.Title className=" tw:pt-3 h2 tw:mb-0 tw:pb-0">
            Project Members
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw:flex tw:items-center tw:mb-1">
            <i className="mdi mdi-folder-zip tw:text-2xl tw:text-primary" />
            <h3 className="tw:mb-0 tw:pl-2 tw:text-dark">{project.Name}</h3>
          </div>
          <h4 className="tw:text-dark tw:pt-2 tw:mb-0">Share with</h4>
          <span className="tw:text-sm">
            The following people will be added to this Project and all its
            chats.
          </span>
          <SelectPicker
            className="tw:pt-2 tw:pb-4"
            size="sm"
            cleanable={true}
            preventOverflow
            data={availableUsers}
            searchable={true}
            value={item}
            loading={recordsLoading}
            searchBy={(keyword, label) => {
              const recordName = (label as string[])[0];
              const email = (label as string[])[1];

              return (
                recordName.toLowerCase().includes(keyword.toLowerCase()) ||
                email.toLowerCase().includes(keyword.toLowerCase())
              );
            }}
            block
            placeholder="Select Members"
            onSelect={(_, item) => handleAddMembers(item.record)}
            renderMenuItem={(label, item) => {
              return (
                <RecordNameCell
                  record={item.record}
                  imageField={imageField || ""}
                  imageFieldPresentation={imageFieldPresentation}
                  metadataField={metadataField}
                  metadataFieldType={metadataFieldType}
                  disableViewRecord={true}
                  onOpen={null}
                  stopPropagation={false}
                />
              );
            }}
          />
          <div className="tw:overflow-y-auto tw:flex tw:flex-col">
            {members.map((m: any) => (
              <Member
                key={m.id}
                member={m}
                imageField={imageField}
                imageFieldPresentation={imageFieldPresentation}
                metadataField={metadataField}
                metadataFieldType={metadataFieldType}
                isOwner={isOwner}
                user={user}
                handleDeleteMember={handleDeleteMember}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="tw:flex tw:justify-center tw:pb-3">
          <Button
            color="primary"
            onClick={handleUpdateMembers}
            disabled={
              projectMembers?.length === finalMembers?.length || updating
            }
          >
            Update Members
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );

  function handleClose() {
    onClose();
    handleMembersChange();
  }

  /** This function checks if the current Project still is a Personal or Team one, if not then we switch the tab and refetch the Projects. */
  function handleMembersChange() {
    let updated = false;

    if (tab === "Team" && project?.Members?.length === 1) {
      setTab("Personal");
      updated = true;
    } else if (tab === "Personal" && (project?.Members || [])?.length > 1) {
      updated = true;
      setTab("Team");
    }

    if (updated) {
      client.refetchQueries({
        queryKey: [tenantID, itemID, "BrieGPTProjects"],
      });
    }
  }

  function handleDeleteMember(memberID: string) {
    setDeletedMembers((prev) => [...prev, memberID]);
    setNewMembers((prev) => prev.filter((m) => m.id !== memberID));
  }

  function handleAddMembers(newMember: any) {
    setDeletedMembers((prev) => prev.filter((m) => m !== newMember.id));

    // To not add the same member twice
    if (!projectMembers.some((pm) => pm.id === newMember.id)) {
      setNewMembers((prev) => {
        return [...prev, newMember];
      });
    }

    setItem("");
  }

  function handleUpdateMembers() {
    updateProject({
      projectID: project.id,
      payload: {
        newMembers,
        removedMembers: deletedMembers,
      },
      onSuccess: () => {
        setNewMembers([]);
        setDeletedMembers([]);
      },
      onError: () => {},
    });
  }
}

export default ProjectMembersModal;
